<template>
  <div style="justify-content: center; display: flex">
    <el-row
      :gutter="gutterValue"
      style="max-width: 2200px; padding-bottom: 25px; width: 100%"
    >
      <el-col
        :xs="24"
        :sm="12"
        :md="8"
        :lg="6"
        :xl="4"
        v-for="(p, productIndex) in props.products"
        :key="props.products.id"
        class="category-col"
      >
        <el-card
          shadow="hover"
          @click="handleCardClick(p, productIndex)"
          class="product_card"
          :style="{
            marginLeft: isXs ? '0px' : '11.5px',
            marginRight: isXs ? '0px' : '11.5px',
          }"
          >
          <span v-if="props.fromCatalog" class="card-category-name">{{
            p.category_name
          }}</span>
          <!-- Карусель логотипов -->
          <el-carousel
            :interval="getRandomInterval()"
            arrow="never"
            indicator-position="none"
            :ref="(el) => (carouselRefs[productIndex] = el)"
            :autoplay="props.autoplay && !pinnedLogos[productIndex]"
            @change="handleCarouselChange(productIndex, $event)"
          >
            <!-- Если есть логотипы, отображаем их -->
            <el-carousel-item
              v-if="p.logos && p.logos.length > 0"
              v-for="logo in p.logos"
              :key="logo.id"
            >
              <!-- 200 и 800 — те же адреса, что на странице товара: браузер берёт их из кеша -->
              <ProgressiveImg
                :low-src="getImageUrl(p, logo, 200)"
                :src="getImageUrl(p, logo, 800)"
                :alt="`${p.product_name} - ${logo.color_name}`"
                width="400"
                height="400"
              />
            </el-carousel-item>
            <!-- Если нет логотипов, отображаем карусель с изображением no_img -->
            <el-carousel-item v-else>
              <img
                src="/img/no_img.png"
                :alt="`Отсутствует изображение для ${p.product_name}`"
                class="logo-image"
                loading="lazy"
                decoding="async"
                width="400"
                height="400"
              />
            </el-carousel-item>
          </el-carousel>

          <!-- Цвета товаров -->
          <div class="color_container">
            <div
              class="color_tem"
              :class="{ 'color_tem--active': activeLogoIndex(productIndex) === index }"
              v-for="(logo, index) in p.logos"
              :key="logo.id"
              v-if="p.logos && p.logos.length > 0"
              :style="{ backgroundColor: logo.bg_color, color: logo.txt_color }"
              @mouseover="setActiveLogo(productIndex, index)"
              @click.stop="pickLogo(productIndex, index)"
            >
              <el-tooltip :content="logo.color_name" placement="bottom">
                {{ logo.code }}
              </el-tooltip>
            </div>
            <div v-else class="color_tem_no_foto">
              <span style="white-space: nowrap; font-weight: 200"
                >Нет доступных цветов</span
              >
            </div>
          </div>

          <div class="product_info">
            <div style="display: flex; align-items: center">
              <el-icon style="font-size: 20px">
                <DCaret />
              </el-icon>
              <h3>{{ formatSize(p.size_sm) }} см</h3>
            </div>
            <div class="flex-grow"></div>
            <div>
              <!-- Цены видны только тем, кому доступен магазин -->
              <h3 v-if="pricesVisible && p.price !== null">{{ p.price }} ₽</h3>
            </div>
          </div>
          <!-- Название — под ценой, как на маркетплейсах: фото во всём ряду начинаются
               на одной высоте, а разная длина названий уходит в отступ над кнопкой
               (кнопка прижата к низу карточки). Не длиннее двух строк, целиком —
               во всплывающей подсказке. Название — настоящая ссылка: по ней ходит
               поисковик, а человек может открыть товар в новой вкладке. Клик по самой
               карточке по-прежнему ведёт на товар с выбранным цветом -->
          <div class="name_container" :title="p.product_name">
            <NuxtLink
              :to="`/catalog/${p.category_alias}/${p.product_alias}`"
              class="name_link"
              @click.stop
            >
              <h2>{{ p.product_name }}</h2>
            </NuxtLink>
          </div>
          <!-- Наличие и изготовление — по цвету, который сейчас показан: от этого
               и зависит дата на кнопке, иначе её скачки непонятны -->
          <div v-if="p.logos?.length" class="supply">
            <span
              class="supply__item"
              :class="supplyFor(p, productIndex).stock > 0 ? 'supply__item--ok' : 'supply__item--off'"
            >
              <el-icon><Box /></el-icon>
              {{ supplyFor(p, productIndex).stock > 0 ? `В наличии ${supplyFor(p, productIndex).stock} шт` : "Нет в наличии" }}
            </span>
            <span
              class="supply__item"
              :class="supplyFor(p, productIndex).days ? 'supply__item--made' : 'supply__item--off'"
            >
              <el-icon><IconFactory :crossed="!supplyFor(p, productIndex).days" /></el-icon>
              {{ supplyFor(p, productIndex).days ? `Изготовим за ${supplyFor(p, productIndex).days} раб. дн.` : "Не производится" }}
            </span>
          </div>
          <ClientOnly>
            <div v-if="shopAccess" class="cart_container">
              <!-- @click.stop внутри самой кнопки: переход на товар висит
                   на контейнерном обработчике карточки -->
              <AddToCartButton
                :product-id="p.product_id"
                :color-id="cartColorId(p, productIndex)"
                :orderable="p.orderable !== false"
                :sold-out="!supplyFor(p, productIndex).stock && !supplyFor(p, productIndex).days"
                :delivery-label="deliveryFor(p, productIndex).label"
                :delivery-hint="deliveryFor(p, productIndex).hint"
              />
            </div>
          </ClientOnly>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { Box, DCaret } from "@element-plus/icons-vue";
import { formatSize } from "~/utils/formater.js";
import { formatDeliveryDate, workingDaysToCalendar } from "~/utils/deliveryDate.js";
import { useWindowSize } from "@vueuse/core";

const { isMobile } = useDevice();
const { width } = useWindowSize();
// На сервере окна нет, и useWindowSize отдаёт Infinity — первое значение
// берём по типу устройства из User-Agent. Иначе телефону приходила разметка
// с отступами десктопа, и карточки на миг съезжали при гидратации
const gutterValue = ref(isMobile ? 0 : 25);
const isXs = ref(!!isMobile);
const router = useRouter(); // { products, autoplay }
const props = defineProps(["products", "autoplay", "fromCatalog"]);
const category = useRoute().params.category;
const carouselRefs = ref([]); // Массив для хранения ссылок на карусели
const activeLogos = ref({}); // Объект для хранения активных логотипов по индексу продукта
const shopAccess = useShopAccess();
const pricesVisible = usePricesVisible();
const { city } = useCity();

// Время в пути спрашиваем один раз на страницу: у всех наших посылок оно
// примерно одинаковое, а различается только срок изготовления товара
const transit = ref(null);

const loadTransit = async () => {
  if (!shopAccess.value || !city.value?.code) {
    transit.value = null;
    return;
  }
  try {
    transit.value = await $fetch("/api/delivery/transit", {
      query: { city_code: city.value.code },
    });
  } catch {
    transit.value = null;
  }
};

watch(() => city.value?.code, loadTransit, { immediate: true });

// Срок для конкретной карточки: цвет, который сейчас показан, либо есть
// на складе, либо изготавливается
const deliveryFor = (p, productIndex) => {
  if (!transit.value || p.orderable === false) return { label: "", hint: "" };

  const logo = p?.logos?.length ? p.logos[activeLogos.value[productIndex] || 0] : null;
  const inStock = Number(logo?.stock_qty) > 0;
  const production = inStock ? 0 : Number(p.production_days) || 0;
  const extra = workingDaysToCalendar(production);

  const label = formatDeliveryDate(transit.value.max + extra);

  const hint = inStock
    ? `В наличии · доставка ${transit.value.min}–${transit.value.max} дн. в ${city.value?.city}`
    : `Под заказ: изготовление ${production} раб. дн. · доставка ${transit.value.min}–${transit.value.max} дн. в ${city.value?.city}`;

  return { label, hint };
};

// Склад — по цвету, который сейчас показан; срок изготовления — у товара целиком.
// 0 дней — товар не производится, продаётся только то, что есть на складе
const supplyFor = (p, productIndex) => {
  const logo = p?.logos?.length ? p.logos[activeLogos.value[productIndex] || 0] : null;
  return {
    stock: Math.max(0, Number(logo?.stock_qty) || 0),
    days: Number(p?.production_days) > 0 ? Number(p.production_days) : 0,
  };
};

// Цвет для корзины — тот, что сейчас показан в карусели карточки
const cartColorId = (p, productIndex) => {
  if (!p?.logos?.length) return null;
  const logo = p.logos[activeLogos.value[productIndex] || 0];
  return logo?.color_id ?? null;
};

// Реальную ширину применяем ТОЛЬКО после гидратации. Если делать это в
// watchEffect, он срабатывает ещё в setup на клиенте и меняет отступы до того,
// как Vue сверит разметку с серверной, — отсюда и была ошибка гидратации.
// Первый клиентский рендер повторяет серверный (по типу устройства), а по
// фактической ширине поправляемся уже в onMounted
const applyWidth = () => {
  gutterValue.value = width.value < 768 ? 0 : 25;
  isXs.value = width.value < 768;
};

onMounted(() => {
  applyWidth();
  watch(width, applyWidth);
});

// Функция для получения URL изображения по его ID
const getImageUrl = (p, l, size) => {
  if (!p?.logos && !p?.logos?.length) return `/img/product.noimage.png`;
  return `/api/image/${p.category_alias}/${p.product_alias}/color/${
    l.color_alias
  }/${l.id}${size ? "?size=" + size : ""}`; // Предположительно у вас есть API для получения изображения по ID
};

// Функция для генерации случайного значения интервала в диапазоне 4000–8000 мс
const getRandomInterval = () => {
  return Math.floor(Math.random() * (8000 - 4000 + 1)) + 4000;
};

// Установка активного элемента карусели при наведении на цвет
// Наведения на телефоне нет, поэтому цвет выбирается и по нажатию.
// activeLogos заполняется лениво — до первого выбора активен первый цвет
const activeLogoIndex = (productIndex) => activeLogos.value[productIndex] || 0;

// Карточки, где покупатель выбрал цвет сам. Автопролистывание там
// останавливается: иначе оно тут же уводило бы выбор на другой цвет,
// и в корзину уезжало бы не то, на что покупатель нажал
const pinnedLogos = ref({});

const pickLogo = (productIndex, logoIndex) => {
  pinnedLogos.value[productIndex] = true;
  setActiveLogo(productIndex, logoIndex);
};

const setActiveLogo = (productIndex, logoIndex) => {
  if (carouselRefs.value[productIndex]) {
    carouselRefs.value[productIndex].setActiveItem(logoIndex); // Устанавливаем активный элемент в нужной карусели
    activeLogos.value[productIndex] = logoIndex; // Сохраняем активный логотип
  }
};

// Обработка события смены логотипа в карусели
const handleCarouselChange = (productIndex, newLogoIndex) => {
  activeLogos.value[productIndex] = newLogoIndex; // Сохраняем активный логотип
};

// Функция для обработки клика по карточке
const handleCardClick = (p, productIndex) => {
  const activeLogoIndex = activeLogos.value[productIndex] || 0; // По умолчанию выбираем первый логотип
  const activeLogo = p.logos ? p.logos[activeLogoIndex] : null;
  const colorAlias = activeLogo ? activeLogo.color_alias : ""; // Если логотипа нет, используем значение по умолчанию

  // Переход на страницу с добавленным параметром color
  router.push(
    `/catalog/${p.category_alias}/${p.product_alias}${
      activeLogo ? `?color=${colorAlias}` : ""
    }`
  );
};
</script>

<style scoped>
.category-col {
  width: 400px;
}
.product_card {
  max-width: 333px;
  min-width: 265px;
  margin: 0 auto;
  /* Карточки в ряду одной высоты, кнопка прижата к низу: разная длина
     названий не сдвигает ни фото, ни кнопки соседей */
  height: 100%;
  display: flex;
  flex-direction: column;
}

.product_card :deep(.el-card__body) {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.category-col {
  display: flex;
  margin-bottom: 25px;
}
.card-category-name {
  font-size: 14px;
  font-weight: 600;
}

.name_container {
  display: flex;
  margin: 4px 0 6px;
}

/* Ссылка на товар не должна отличаться от прежнего названия карточки */
.name_link {
  color: inherit;
  text-decoration: none;
}

.name_link:hover {
  color: var(--el-color-primary);
}

/* Кнопка во всю ширину карточки: маленькую было тяжело попасть,
   особенно на телефоне */
.cart_container {
  display: flex;
  margin-top: auto;
  padding-top: 8px;
}

.cart_container :deep(.el-button) {
  width: 100%;
}

.name_container h2 {
  font-size: 15px;
  line-height: 1.3;
  font-weight: 600;
  display: -webkit-box;
  /* Для работы с multiline ellipsis */
  -webkit-box-orient: vertical;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  /* Не больше двух строк */
  overflow: hidden;
  /* Обрезаем текст, если он выходит за границы */
  text-overflow: ellipsis;
  margin: 0;
}

.product_info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 25px;
}

.supply {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin: 2px 0 4px;
  font-size: 13px;
  line-height: 1.3;
}

.supply__item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}

.supply__item .el-icon {
  font-size: 16px;
}

/* Те же цвета, что у наличия на странице товара */
.supply__item--ok {
  color: var(--el-color-success-dark-2);
}

.supply__item--made {
  color: var(--el-color-warning-dark-2);
}

.supply__item--off {
  color: var(--el-text-color-secondary);
}

.product_info h3 {
  font-weight: 600;
}

.product_info div {
  display: flex;
  align-items: center;
}

.color_container {
  display: flex;
  justify-content: center;
  margin-top: 3px;
  /* Место под указатель выбранного цвета */
  padding-bottom: 8px;
  align-items: center;
}

.color_tem {
  position: relative;
  flex-direction: row;
  text-align: center;
  width: 24px;
  height: 24px;
  margin: 0px 1px 1px 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  font-size: 12px;
  font-weight: bold;
  border: 1px solid var(--el-color-info-light-8);
  cursor: pointer;
}

/* Указатель на выбранный цвет: по одним квадратикам не понять, какой из них
   уедет в корзину. Треугольник снизу «смотрит» на активную метку */
.color_tem--active {
  outline: 2px solid #008001;
  outline-offset: 1px;
}

.color_tem--active::after {
  content: "";
  position: absolute;
  top: calc(100% + 5px);
  left: 50%;
  transform: translateX(-50%);
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-bottom: 5px solid #008001;
}

.color_tem_no_foto {
  flex-direction: row;
  text-align: center;
  height: 27px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  font-size: 12px;
  font-weight: bold;
}

.el-card {
  transition: padding 0.3s ease;
  cursor: pointer;
  border-radius: 7px;
  margin-top: 15px;
}

.el-card:hover {
  padding-left: 2px;
  padding-right: 2px;
}

.image-container {
  width: 100%;
  /* Соотношение 1:1 */
  position: relative;
  overflow: hidden;
}

.image-container img {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: auto;
  transform: translate(-50%, -50%);
}

.image-container img.landscape {
  width: 100%;
  height: auto;
}

.logo-image {
  /* width: 100%; */
  /* height: 275px; */
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.flex-grow {
  flex-grow: 1;
}
</style>
